// FILE: TenantGitHubProfile.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../BasicComponents/UserContext';
import {
  Github,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  Save,
  Edit3,
  GitBranch,
  RefreshCw,
  UserCheck,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/* ── GitHub username validation ── */
// https://docs.github.com/en/account-and-profile/setting-up-and-managing-your-personal-account-on-github/managing-user-account-settings/changing-your-github-username
const GITHUB_USERNAME_RE = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/;

const validateUsername = (v) => {
  if (!v || !v.trim()) return 'GitHub username is required.';
  const s = v.trim();
  if (s.length < 1)   return 'Username too short.';
  if (s.length > 39)  return 'Username must be 39 characters or fewer.';
  if (!GITHUB_USERNAME_RE.test(s)) return 'Username may only contain letters, numbers, and hyphens, and cannot start or end with a hyphen.';
  if (s.includes('--')) return 'Username cannot contain consecutive hyphens.';
  return '';
};

/* ── Status display config ── */
const STATUS_META = {
  ACCEPTED:         { label: 'Access Granted',   color: '#059669', bg: '#D1FAE5', Icon: CheckCircle2, desc: 'You have access to the college repository.' },
  INVITED:          { label: 'Invitation Sent',  color: '#D97706', bg: '#FEF3C7', Icon: Clock,         desc: 'A repository invitation has been sent. Check your GitHub email.' },
  READY_FOR_INVITE: { label: 'Pending Invite',   color: '#6366F1', bg: '#EEF2FF', Icon: Clock,         desc: 'Your username is on file. An admin will send your invitation shortly.' },
  DECLINED:         { label: 'Invite Declined',  color: '#DC2626', bg: '#FEE2E2', Icon: XCircle,       desc: 'You declined the invitation. Contact your admin to re-send.' },
  REVOKED:          { label: 'Access Revoked',   color: '#9CA3AF', bg: '#F3F4F6', Icon: XCircle,       desc: 'Your access was revoked. Contact your admin for assistance.' },
  SUSPENDED:        { label: 'Suspended',        color: '#92400E', bg: '#FEF3C7', Icon: AlertCircle,   desc: 'Your access is suspended. Contact your admin.' },
  NOT_CONNECTED:    { label: 'Not Connected',    color: '#9CA3AF', bg: '#F3F4F6', Icon: AlertCircle,   desc: 'Add your GitHub username below to get started.' },
};

const StatusCard = ({ status, repoUrl }) => {
  const meta = STATUS_META[status] || STATUS_META.NOT_CONNECTED;
  const { Icon } = meta;
  return (
    <div className="rounded-2xl p-5 flex items-start gap-4" style={{ background: meta.bg, border: `1.5px solid ${meta.color}30` }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}20` }}>
        <Icon size={20} style={{ color: meta.color }} />
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm" style={{ color: meta.color }}>{meta.label}</div>
        <div className="text-sm text-gray-600 mt-0.5">{meta.desc}</div>
        {status === 'ACCEPTED' && repoUrl && (
          <a
            href={repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-indigo-600 hover:underline"
          >
            <GitBranch size={12} />
            Open Repository
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const TenantGitHubProfile = () => {
  const { user } = useContext(UserContext);
  const [studentId,  setStudentId]  = useState(null);
  const [collegeId,  setCollegeId]  = useState(null);

  // GitHub state from API
  const [githubStatus,   setGithubStatus]   = useState(null);  // full status object
  const [inviteStatus,   setInviteStatus]   = useState('NOT_CONNECTED');
  const [githubUsername, setGithubUsername] = useState('');
  const [repoUrl,        setRepoUrl]        = useState('');
  const [repoName,       setRepoName]       = useState('');

  // Edit mode
  const [editing,     setEditing]     = useState(false);
  const [inputValue,  setInputValue]  = useState('');
  const [validationError, setValidationError] = useState('');
  const [checkingUser,    setCheckingUser]    = useState(false); // verifying GitHub username exists
  const [githubUserError, setGithubUserError] = useState('');

  // Loading states
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');

  /* ── init ── */
  useEffect(() => {
    const sid = user?.id || user?.user_id || user?.userId;
    let cid = user?.college_id || user?.collegeId || user?.tenant?.college_id;

    // Last resort: check localStorage directly if context is missing it
    if (!cid) {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        cid = stored?.college_id || stored?.collegeId || stored?.tenant?.college_id;
      } catch (e) {}
    }

    if (sid) setStudentId(sid);
    if (cid) setCollegeId(cid);
    
    console.log('[TenantGitHubProfile] Initialized IDs:', { studentId: sid, collegeId: cid });
  }, [user]);

  /* ── fetch GitHub status ── */
  const fetchStatus = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/github/status`, { params: { userId: sid } });
      const data = res.data || {};
      setGithubStatus(data);
      setGithubUsername(data.github_username || data.githubUsername || '');
      const inviteRaw = data.invite_status || data.inviteStatus || (data.github_username ? 'READY_FOR_INVITE' : 'NOT_CONNECTED');
      setInviteStatus(inviteRaw.toUpperCase());
      setRepoUrl(data.repo_url || data.repoUrl || '');
      setRepoName(data.repo_name || data.repoName || '');
    } catch (err) {
      if (err.response?.status === 404) {
        setInviteStatus('NOT_CONNECTED');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (studentId) fetchStatus(studentId);
  }, [studentId, fetchStatus]);

  /* ── toast ── */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  /* ── verify GitHub username exists via public API ── */
  const verifyGitHubUser = async (username) => {
    setCheckingUser(true);
    setGithubUserError('');
    try {
      await axios.get(`https://api.github.com/users/${username}`);
      return true;
    } catch (err) {
      if (err.response?.status === 404) {
        setGithubUserError(`GitHub user "@${username}" does not exist.`);
      } else {
        // Network error or rate-limit — allow it through
        return true;
      }
      return false;
    } finally {
      setCheckingUser(false);
    }
  };

  /* ── save username ── */
  const handleSave = async () => {
    const val = inputValue.trim();
    const err = validateUsername(val);
    if (err) { setValidationError(err); return; }
    setValidationError('');

    // Verify GitHub user exists
    const exists = await verifyGitHubUser(val);
    if (!exists) return;

    setSaving(true);
    try {
      console.log('[TenantGitHubProfile] Saving GitHub handle:', { studentId, collegeId, githubUsername: val });
      await axios.post(`${API_BASE_URL}/api/github/connect`, {
        studentId,
        collegeId,
        githubUsername: val,
      });
      showToast('GitHub username saved successfully.');
      setEditing(false);
      setGithubUsername(val);
      setInviteStatus(prev => {
        const next = prev === 'NOT_CONNECTED' ? 'READY_FOR_INVITE' : prev;
        return next.toUpperCase();
      });
      fetchStatus(studentId);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save GitHub username.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setInputValue(githubUsername || '');
    setValidationError('');
    setGithubUserError('');
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setValidationError('');
    setGithubUserError('');
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setValidationError('');
    setGithubUserError('');
  };

  /* ── render ── */
  return (
    <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6">

      <div className="bg-white rounded-3xl p-6 md:p-8" style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm" style={{ background: '#EEF2FF', border: '1px solid #E0E7FF' }}>
              <Github size={24} style={{ color: '#4F46E5' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">GitHub Integration</h1>
              <p className="text-sm text-gray-500 mt-1">Connect your account to receive repository access</p>
            </div>
          </div>
          <button
            onClick={() => fetchStatus(studentId)}
            disabled={loading}
            className="self-start sm:self-center inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-all active:scale-95 disabled:opacity-50"
            style={{ border: '1px solid var(--border-subtle)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          >
            <RefreshCw size={15} className={loading ? 'animate-spin text-indigo-500' : 'text-gray-500'} />
            {loading ? 'Updating...' : 'Sync Status'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Status & Username */}
          <div className="lg:col-span-6 space-y-6">
            {/* ── Connection Status ── */}
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">Current Status</h3>
              {loading ? (
                <div className="rounded-2xl p-6 bg-gray-50 flex items-center gap-3 text-sm text-gray-500 animate-pulse" style={{ border: '1px solid var(--border-subtle)' }}>
                  <Loader2 size={18} className="animate-spin text-indigo-500" /> 
                  Synchronizing with GitHub...
                </div>
              ) : (
                <StatusCard status={inviteStatus} repoUrl={repoUrl} />
              )}
            </div>

            {/* ── GitHub Username Card ── */}
            <div className="rounded-2xl p-6 bg-white" style={{ border: '1px solid var(--border-subtle)' }}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <UserCheck size={16} className="text-indigo-500" />
                  <h2 className="font-bold text-gray-900">GitHub Identity</h2>
                </div>
                {!editing && githubUsername && (
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                  >
                    <Edit3 size={13} />
                    Change
                  </button>
                )}
              </div>

              {!editing ? (
                <div>
                  {githubUsername ? (
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl bg-gray-50 border border-gray-100 shadow-inner">
                        <Github size={18} className="text-gray-400" />
                        <span className="font-mono font-bold text-gray-900 text-lg">@{githubUsername}</span>
                      </div>
                      <a
                        href={`https://github.com/${githubUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
                      >
                        View Profile <ExternalLink size={14} />
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-8 px-4 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/50">
                      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                        <Github size={28} className="text-gray-300" />
                      </div>
                      <h4 className="text-gray-900 font-bold mb-1">No username linked</h4>
                      <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">Connect your GitHub account to receive an automated repository invitation.</p>
                      <button
                        onClick={handleEdit}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all active:scale-95"
                      >
                        <Github size={16} />
                        Link GitHub Account
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-tight mb-2 ml-1">
                      Enter GitHub Username
                    </label>
                    <div className="relative group">
                      <div 
                        className="flex items-center rounded-xl overflow-hidden transition-all duration-200"
                        style={{ 
                          border: `2px solid ${validationError || githubUserError ? '#EF4444' : 'var(--border-subtle)'}`,
                          boxShadow: validationError || githubUserError ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : 'none'
                        }}
                      >
                        <div className="pl-4 pr-2 py-3.5 bg-gray-50/50 text-gray-400 border-r border-gray-100">
                          <Github size={18} />
                        </div>
                        <input
                          type="text"
                          value={inputValue}
                          onChange={handleInputChange}
                          placeholder="e.g. janesmith"
                          className="flex-1 px-4 py-3.5 text-base font-mono bg-white outline-none text-gray-900 placeholder-gray-300"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
                        />
                        {checkingUser && (
                          <div className="pr-4">
                            <Loader2 size={18} className="animate-spin text-indigo-500" />
                          </div>
                        )}
                      </div>
                    </div>

                    {(validationError || githubUserError) && (
                      <div className="mt-3 p-3 bg-red-50 rounded-xl border border-red-100 flex items-start gap-2.5 animate-in fade-in slide-in-from-top-1">
                        <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm font-medium text-red-700 leading-tight">
                          {validationError || githubUserError}
                        </p>
                      </div>
                    )}
                  </div>

                  <p className="text-[11px] text-gray-400 leading-relaxed px-1">
                    Please provide your <strong>public GitHub handle</strong>. We'll use this to grant you access to the official course repositories.
                  </p>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saving || checkingUser || !inputValue.trim()}
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 shadow-lg shadow-indigo-100 transition-all active:scale-95"
                    >
                      {saving || checkingUser ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      {saving ? 'Saving...' : checkingUser ? 'Verifying...' : 'Update Handle'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-6 py-3 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Repository & Instructions */}
          <div className="lg:col-span-6 space-y-6">
            {/* ── Repository Info ── */}
            <div className="rounded-2xl p-6 bg-white" style={{ border: '1px solid var(--border-subtle)' }}>
              <div className="flex items-center gap-2 mb-5">
                <GitBranch size={16} className="text-indigo-500" />
                <h2 className="font-bold text-gray-900">Project Repository</h2>
              </div>
              
              {(repoUrl || repoName) ? (
                <div className="space-y-5">
                  <div>
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Assigned Repository</div>
                    <div className="text-sm font-bold text-gray-800 break-all">{repoName || 'Pending Assignment...'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Access Link</div>
                    {repoUrl ? (
                      <a 
                        href={repoUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gray-900 hover:bg-black transition-all shadow-sm"
                      >
                        Launch Repository <ExternalLink size={14} />
                      </a>
                    ) : (
                      <div className="text-sm text-gray-400 italic">Link will appear once invitation is accepted</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-2 text-sm text-gray-500 leading-relaxed">
                  Your repository details will be displayed here once your college admin processes your request.
                </div>
              )}
            </div>

            {/* ── Help card ── */}
            <div className="rounded-2xl p-6 bg-indigo-50/50 border border-indigo-100">
              <h3 className="text-sm font-bold text-indigo-900 mb-4 flex items-center gap-2">
                <div className="w-1.5 h-4 bg-indigo-500 rounded-full" />
                Next Steps
              </h3>
              <ul className="space-y-4">
                {[
                  { step: 1, text: "Provide your GitHub username above", done: !!githubUsername },
                  { step: 2, text: "Wait for an email invitation from GitHub", done: inviteStatus === 'INVITED' || inviteStatus === 'ACCEPTED' },
                  { step: 3, text: "Accept the invite in your email or on GitHub", done: inviteStatus === 'ACCEPTED' },
                  { step: 4, text: "Your repository will then be available here", done: inviteStatus === 'ACCEPTED' && !!repoUrl }
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${item.done ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-600'}`}>
                      {item.done ? '✓' : item.step}
                    </div>
                    <span className={`text-xs font-medium leading-tight ${item.done ? 'text-indigo-900' : 'text-indigo-700/70'}`}>
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[300] px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default TenantGitHubProfile;
